import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProductFruits } from 'react-product-fruits';
import { useTranslation } from 'react-i18next';

import API from '@/lib/api';
import type { UserProps } from '@/lib/auth';
import {
  normalizeProductFruitsLanguage,
  PRODUCT_FRUITS_WORKSPACE_CODE,
} from '@/lib/productFruits';

function getProductFruitsUser(user: UserProps) {
  return {
    username: user.email || user.id,
    email: user.email,
    firstname: user.firstName,
    lastname: user.lastName,
    role: user.role,
    props: {
      accountType: user.account?.accountType,
      accountId: user.currentAccountId,
    },
  };
}

export function ProductFruitsIntegration() {
  const { i18n } = useTranslation();
  const language = normalizeProductFruitsLanguage(i18n.resolvedLanguage ?? i18n.language);

  const { data: user } = useQuery<UserProps | null>({
    queryKey: ['/me'],
    enabled: Boolean(PRODUCT_FRUITS_WORKSPACE_CODE),
    queryFn: async () => {
      try {
        const res = await API.get('/me');
        return res.data;
      } catch {
        return null;
      }
    },
  });

  const productFruitsUser = useMemo(() => {
    if (!user) {
      return null;
    }

    return getProductFruitsUser(user);
  }, [user]);

  if (!PRODUCT_FRUITS_WORKSPACE_CODE || !productFruitsUser) {
    return null;
  }

  return (
    <ProductFruits
      key={`${productFruitsUser.username}-${language}`}
      workspaceCode={PRODUCT_FRUITS_WORKSPACE_CODE}
      language={language}
      user={productFruitsUser}
      lifeCycle="unmount"
    />
  );
}
